import { useApolloClient } from "@apollo/client";
import {
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ModalOpenButton } from "@msys/ui";
import { normalizeSmartProcess } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useOverridePopover_TemplatesByIdsQuery } from "./OverridePopover.generated.js";
import { TemplateSelectModal } from "../../templates/quote/TemplateSelectModal.js";
import type { TemplateBase } from "../../templates/quote/TemplatesDisplay.js";

export type OverrideDraft = {
  defaultTemplateId: string | null;
  defaultSmartProcess: string | null;
};

// A draft is only a real override once it carries a template or a non-blank
// smart process — a whitespace-only smart process is trimmed to nothing on
// save, so it must not mark the cell as customized or persist an empty row.
export const overrideHasValue = (draft: OverrideDraft): boolean =>
  draft.defaultTemplateId != null ||
  normalizeSmartProcess(draft.defaultSmartProcess) !== null;

export type OverridePopoverProps = {
  anchorEl: HTMLElement | null;
  projectTypeLabel: string;
  draft: OverrideDraft;
  onChange: (next: OverrideDraft) => void;
  onClose: () => void;
};

export const OverridePopover = ({
  anchorEl,
  projectTypeLabel,
  draft,
  onChange,
  onClose,
}: OverridePopoverProps) => {
  const { t } = useTranslate(["OrganisationProfile", "Global", "Templates"]);
  const client = useApolloClient();

  // Titles are known immediately when the user picks one; only a template
  // carried over from a saved override needs resolving from the server. The
  // picked title is kept alongside its id so it is only trusted while the
  // draft still points at that template — otherwise a title could outlive the
  // id it described and mislabel a different template.
  const [picked, setPicked] = React.useState<{
    id: string;
    title: string;
  } | null>(null);
  const templateId = draft.defaultTemplateId;
  const pickedTitle = picked && picked.id === templateId ? picked.title : null;

  const templatesQuery = useOverridePopover_TemplatesByIdsQuery({
    client,
    variables: { templateIds: templateId ? [templateId] : [] },
    skip: !templateId || pickedTitle !== null,
  });

  const fetchedTitle = React.useMemo(() => {
    const conn = templatesQuery.data?.searchTemplates;
    if (conn?.__typename === "QuoteTemplateConnection" && templateId) {
      return conn.edges.find(e => e.node.id === templateId)?.node.title ?? null;
    }
    return null;
  }, [templatesQuery.data, templateId]);

  const templateLabel = templateId
    ? (pickedTitle ?? fetchedTitle ?? templateId)
    : "—";

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
        <Typography variant="h4">{projectTypeLabel}</Typography>

        <Stack spacing={0.5}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("Template", { ns: "OrganisationProfile" })}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {templateLabel}
              </Typography>
            </Box>
            <ModalOpenButton
              Modal={TemplateSelectModal}
              modalProps={{
                title: t("Select template", { ns: "Templates" }),
                initialFilters: {},
                initialSorting: [],
                handleComplete: (template: TemplateBase, close) => {
                  onChange({ ...draft, defaultTemplateId: template.id });
                  setPicked({ id: template.id, title: template.title });
                  close();
                },
              }}
            >
              <Button variant="outlined" size="small">
                {t("Choose…", { ns: "Global" })}
              </Button>
            </ModalOpenButton>
            {draft.defaultTemplateId !== null && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  onChange({ ...draft, defaultTemplateId: null });
                  setPicked(null);
                }}
              >
                {t("Clear", { ns: "Global" })}
              </Button>
            )}
          </Stack>
        </Stack>

        <TextField
          size="small"
          label={t("Smart process", { ns: "OrganisationProfile" })}
          value={draft.defaultSmartProcess ?? ""}
          onChange={e =>
            onChange({
              ...draft,
              defaultSmartProcess:
                e.target.value === "" ? null : e.target.value,
            })
          }
        />

        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
          <Button variant="text" size="small" onClick={onClose}>
            {t("Close", { ns: "Global" })}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
